import * as React from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSaveBillingPack } from '@/queries/billing'
import type { BillingAccount, BillingPack } from '@/types/billing'
import { formatMinor } from './money'

const EMPTY: BillingPack = {
  code: '', name: '', credits: 0, price_units: 0, currency: 'EUR', expires_after_days: 365, is_active: true,
}

/** The packs an account sells. One form to create and to edit a pack. */
export function PacksSection({ account }: { account: BillingAccount }) {
  const { t, i18n } = useTranslation()
  const save = useSaveBillingPack()
  const [editing, setEditing] = React.useState<BillingPack | null>(null)
  const [draft, setDraft] = React.useState<BillingPack>(EMPTY)
  const [errors, setErrors] = React.useState<Partial<Record<keyof BillingPack, string>>>({})

  const open = (pack: BillingPack | null) => {
    setEditing(pack ?? EMPTY)
    setDraft(pack ?? EMPTY)
    setErrors({})
  }

  const field = (key: keyof BillingPack) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [key]: event.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const next: Partial<Record<keyof BillingPack, string>> = {}
    if (!/^[\w-]+$/.test(String(draft.code))) next.code = t('billing.packs.errors.code')
    if (!String(draft.name).trim()) next.name = t('billing.packs.errors.name')
    if (!/^\d+$/.test(String(draft.credits)) || Number(draft.credits) <= 0) next.credits = t('billing.account.errors.whole_number')
    if (!/^\d+$/.test(String(draft.price_units))) next.price_units = t('billing.account.errors.whole_number')
    if (!/^[A-Za-z]{3}$/.test(String(draft.currency))) next.currency = t('billing.packs.errors.currency')
    setErrors(next)
    if (Object.keys(next).length > 0) return
    try {
      await save.mutateAsync({
        id: account.id,
        pack: {
          ...draft,
          credits: Number(draft.credits),
          price_units: Number(draft.price_units),
          currency: String(draft.currency).toUpperCase(),
          expires_after_days: draft.expires_after_days === null || String(draft.expires_after_days) === ''
            ? null : Number(draft.expires_after_days),
        },
      })
      toast.success(t('billing.packs.saved'))
      setEditing(null)
    } catch {
      toast.error(t('billing.errors.save'))
    }
  }

  const input = (key: keyof BillingPack, label: string, extra: React.ComponentProps<typeof Input> = {}) => (
    <div className="space-y-1">
      <Label htmlFor={`pack-${key}`}>{label}</Label>
      <Input id={`pack-${key}`} value={draft[key] === null ? '' : String(draft[key])} onChange={field(key)}
        aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `pack-${key}-error` : undefined} {...extra} />
      <FieldError id={`pack-${key}-error`}>{errors[key]}</FieldError>
    </div>
  )

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold">{t('billing.packs.title')}</h2>
        <Button variant="outline" size="sm" onClick={() => open(null)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('billing.packs.create')}
        </Button>
      </div>
      {account.packs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('billing.packs.empty')}</p>
      ) : (
        <div className="space-y-2">
          {account.packs.map((pack) => (
            <div key={pack.code} className="flex items-center justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{pack.name}</p>
                  {!pack.is_active && <Badge variant="outline">{t('billing.packs.inactive')}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('billing.packs.summary', {
                    credits: pack.credits,
                    price: formatMinor(pack.price_units, pack.currency, i18n.language),
                    days: pack.expires_after_days ?? '∞',
                  })}
                </p>
              </div>
              <Button variant="ghost" size="xs" onClick={() => open(pack)}>{t('billing.common.edit')}</Button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={editing !== null} onOpenChange={(next) => !next && setEditing(null)}>
        <DialogContent>
          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing?.code ? t('billing.packs.edit_title') : t('billing.packs.create_title')}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              {input('code', t('billing.packs.code'), { disabled: !!editing?.code })}
              {input('name', t('billing.packs.name'))}
              {input('credits', t('billing.packs.credits'), { inputMode: 'numeric' })}
              {input('price_units', t('billing.packs.price_units'), { inputMode: 'numeric' })}
              {input('currency', t('billing.packs.currency'))}
              {input('expires_after_days', t('billing.packs.expires_after_days'), { inputMode: 'numeric' })}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>{t('billing.common.cancel')}</Button>
              <Button type="submit" disabled={save.isPending}>{t('billing.common.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
